import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { injectModalData, injectModalRef } from '../../../shared/modal';
import { PlaygroundAssistService } from '../../../core/services';

export interface PromptOptimizerData {
  question: string;
  endpoint: 'rag' | 'answer';
}

type OptimizerStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Opened from "Perfect your prompt with our Prompt Optimizer" on RAG and
 * Answer (see openPromptOptimizer() on each) shows the current question
 * and waits for the user to hit "Optimize" before calling
 * PlaygroundAssistService.optimizePrompt() at all (doesn't fire on open
 * opening the modal costs nothing). Once optimized, shows the original
 * next to the rewrite, and hands the rewrite back only if the user
 * explicitly hits "Use this version". Cancelling (✕, backdrop, Escape, or
 * Cancel) resolves with `undefined`, same "no result means no change"
 * contract as MapAreaPicker.
 *
 * No mock fallback here (unlike CikisiResourcesService/AdminService)
 * a failed call surfaces as a real error state with a Try again, rather
 * than silently substituting something made up for a feature whose whole
 * point is showing the user real model output.
 */
@Component({
  selector: 'app-prompt-optimizer',
  standalone: false,
  templateUrl: './prompt-optimizer.html',
  styleUrl: './prompt-optimizer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptOptimizer {
  readonly data = injectModalData<PromptOptimizerData>();
  readonly modalRef = injectModalRef<string>();

  private readonly assistService = inject(PlaygroundAssistService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly status = signal<OptimizerStatus>('idle');
  protected readonly optimized = signal('');

  constructor() {
    // Don't let a slow optimize call be cut off by an accidental backdrop
    // click while it's running.
    this.modalRef.setCanClose(({ reason }) => reason === 'close' || reason === 'force' || this.status() !== 'loading');
  }

  optimize(): void {
    this.status.set('loading');
    this.assistService.optimizePrompt(this.data.question, this.data.endpoint)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.optimized.set(result);
          this.status.set('ready');
        },
        error: () => this.status.set('error'),
      });
  }

  useOptimized(): void {
    this.modalRef.close(this.optimized());
  }

  cancel(): void {
    this.modalRef.dismiss();
  }
}
